import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useOutlet } from "@/contexts/OutletContext";
import { fmt } from "@/lib/format";
import { Plus, PencilSimple, Trash, FloppyDisk, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const blankSalary = () => ({
  outlet_id: "",
  staff_name: "",
  role: "cashier",
  monthly_salary: "",
  active: true,
});

export default function Salaries() {
  const { outlets } = useOutlet();
  const [salaries, setSalaries] = useState([]);
  const [outletFilter, setOutletFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = outletFilter && outletFilter !== "all" ? `?outlet_id=${outletFilter}` : "";
      const { data } = await api.get(`/salaries${params}`);
      setSalaries(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [outletFilter]);

  const outletName = (id) => outlets.find((o) => o.id === id)?.name || "—";

  const grouped = useMemo(() => {
    const map = {};
    salaries.forEach((s) => {
      const oid = s.outlet_id || "unassigned";
      if (!map[oid]) map[oid] = { outlet_id: oid, name: outletName(oid), total: 0, active: 0, items: [] };
      map[oid].items.push(s);
      if (s.active) {
        map[oid].total += s.monthly_salary || 0;
        map[oid].active += 1;
      }
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [salaries, outlets]);

  const grandTotal = useMemo(
    () => salaries.filter((s) => s.active).reduce((a, s) => a + (s.monthly_salary || 0), 0),
    [salaries]
  );

  const startNew = () => {
    const first = outletFilter !== "all" ? outletFilter : outlets[0]?.id || "";
    setEditing({ ...blankSalary(), outlet_id: first });
  };

  const save = async () => {
    if (!editing.outlet_id || !editing.staff_name || !editing.monthly_salary) {
      toast.error("Outlet, staff name and salary required");
      return;
    }
    const salary = parseFloat(editing.monthly_salary);
    if (isNaN(salary) || salary < 0) { toast.error("Invalid salary"); return; }
    try {
      const payload = {
        outlet_id: editing.outlet_id,
        staff_name: editing.staff_name,
        role: editing.role,
        monthly_salary: salary,
        active: editing.active,
      };
      if (editing.id) {
        await api.patch(`/salaries/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/salaries", payload);
        toast.success("Added");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this salary record?")) return;
    try {
      await api.delete(`/salaries/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold" data-testid="salaries-title">Salaries</h1>
            <p className="text-sm text-boba-inkSoft mt-1">Monthly staff cost per outlet — used in P&L</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={outletFilter}
              onChange={(e) => setOutletFilter(e.target.value)}
              className="h-10 px-3 border border-boba-stoneLine rounded-md bg-white text-sm"
              data-testid="salaries-outlet-filter"
            >
              <option value="all">All outlets</option>
              {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <button
              onClick={startNew}
              className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium flex items-center gap-2"
              data-testid="new-salary-btn"
            >
              <Plus size={16} weight="bold" /> Add Salary
            </button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-boba-cream border border-boba-stoneLine rounded-md p-4">
            <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">Total monthly (active)</div>
            <div className="font-mono text-2xl font-bold mt-2" data-testid="salaries-grand-total">{fmt(grandTotal)}</div>
          </div>
          <div className="bg-boba-cream border border-boba-stoneLine rounded-md p-4">
            <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">Records</div>
            <div className="font-mono text-2xl font-bold mt-2">{salaries.length}</div>
          </div>
          <div className="bg-boba-cream border border-boba-stoneLine rounded-md p-4">
            <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">Outlets covered</div>
            <div className="font-mono text-2xl font-bold mt-2">{grouped.length}</div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {loading && <div className="text-center text-stone-500 py-8">Loading…</div>}
        {!loading && salaries.length === 0 && (
          <div className="text-center text-stone-500 py-12">
            <Users size={40} className="mx-auto mb-2 text-stone-300" />
            No salary records yet. Click Add Salary to enter staff.
          </div>
        )}
        {grouped.map((g) => (
          <div key={g.outlet_id} className="bg-white border border-boba-stoneLine rounded-md overflow-hidden" data-testid={`salary-group-${g.outlet_id}`}>
            <div className="flex items-center justify-between px-5 py-3 bg-boba-cream border-b border-boba-stoneLine">
              <div>
                <div className="font-display text-lg font-bold">{g.name}</div>
                <div className="text-xs text-boba-inkSoft font-mono">{g.active} active staff</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-boba-inkSoft font-mono">Monthly</div>
                <div className="font-mono text-xl font-bold text-boba-matcha" data-testid={`salary-outlet-total-${g.outlet_id}`}>{fmt(g.total)}</div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-boba-inkSoft text-[11px] uppercase tracking-widest font-mono">
                <tr>
                  <th className="px-5 py-2 text-left">Staff</th>
                  <th className="px-5 py-2 text-left">Role</th>
                  <th className="px-5 py-2 text-left">Since</th>
                  <th className="px-5 py-2 text-left">Status</th>
                  <th className="px-5 py-2 text-right">Salary / mo</th>
                  <th className="px-5 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((s) => (
                  <tr key={s.id} className="border-t border-boba-stoneLine hover:bg-stone-50">
                    <td className="px-5 py-3 font-medium">{s.staff_name}</td>
                    <td className="px-5 py-3 capitalize text-boba-inkSoft">{s.role?.replace("_", " ")}</td>
                    <td className="px-5 py-3 font-mono text-xs text-boba-inkSoft">{s.effective_from}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 text-xs rounded font-mono ${s.active ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                        {s.active ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-semibold">{fmt(s.monthly_salary)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setEditing({ ...s, monthly_salary: String(s.monthly_salary) })} className="p-1.5 hover:bg-stone-100 rounded" data-testid={`edit-salary-${s.id}`}><PencilSimple size={16} /></button>
                        <button onClick={() => remove(s.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" data-testid={`delete-salary-${s.id}`}><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="salary-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.id ? "Edit Salary" : "New Salary"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Outlet</label>
                <select
                  value={editing.outlet_id}
                  onChange={(e) => setEditing({ ...editing, outlet_id: e.target.value })}
                  className="w-full mt-1 h-10 px-2 border border-boba-stoneLine rounded-md bg-white"
                  data-testid="salary-outlet"
                >
                  <option value="">Select outlet…</option>
                  {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Staff Name</label>
                  <input
                    value={editing.staff_name}
                    onChange={(e) => setEditing({ ...editing, staff_name: e.target.value })}
                    className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md"
                    data-testid="salary-name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Role</label>
                  <select
                    value={editing.role}
                    onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                    className="w-full mt-1 h-10 px-2 border border-boba-stoneLine rounded-md bg-white"
                    data-testid="salary-role"
                  >
                    <option value="cashier">Cashier</option>
                    <option value="admin">Outlet Admin</option>
                    <option value="kitchen">Kitchen</option>
                    <option value="delivery">Delivery</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Monthly Salary (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editing.monthly_salary}
                  onChange={(e) => setEditing({ ...editing, monthly_salary: e.target.value })}
                  className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                  data-testid="salary-amount"
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} id="salary-active" data-testid="salary-active" />
                <label htmlFor="salary-active" className="text-sm">Active (include in P&L)</label>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-boba-stoneLine">
                <button onClick={() => setEditing(null)} className="h-10 px-4 border border-boba-stoneLine rounded-md hover:bg-stone-50">Cancel</button>
                <button onClick={save} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium flex items-center gap-2" data-testid="salary-save">
                  <FloppyDisk size={16} weight="bold" /> Save
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
